import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../lib/api';
import { Btn, Card, Input, Label, PageHeader, Select } from '../components/Ui';
import { RefreshCw, ExternalLink, X } from 'lucide-react';

const OUTCOME_STYLE = {
  created: 'bg-ok text-white',
  duplicate: 'bg-warn text-ink',
  rejected: 'bg-danger text-white',
  error: 'bg-danger text-white',
};

const PAYLOAD_STATUS_STYLE = {
  archived: 'bg-ok/15 text-ok border-ok',
  reviewed: 'bg-ok/15 text-ok border-ok',
  received: 'bg-surface text-ink border-border',
  check_required: 'bg-warn/15 text-ink border-warn',
  no_pdf: 'bg-danger text-white border-danger',
  failed: 'bg-danger text-white border-danger',
  ignored: 'bg-surface text-muted border-border',
  sent_to_accountant: 'bg-accent/15 text-accent border-accent',
};

function OutcomeBadge({ outcome }) {
  const cls = OUTCOME_STYLE[outcome] || 'bg-surface text-ink';
  return (
    <span
      data-testid={`outcome-${outcome}`}
      className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide2 ${cls}`}
    >
      {outcome}
    </span>
  );
}

const PAYLOAD_STATUS_LABELS = {
  received: 'recibido',
  archived: 'archivado',
  check_required: 'por revisar',
  reviewed: 'revisado',
  sent_to_accountant: 'enviado',
  no_pdf: 'sin pdf',
  failed: 'fallido',
  ignored: 'ignorado',
};

function PayloadStatusBadge({ status }) {
  if (!status) return <span className="text-muted">—</span>;
  const cls = PAYLOAD_STATUS_STYLE[status] || 'bg-surface text-ink border-border';
  const label = PAYLOAD_STATUS_LABELS[status] || status;
  return (
    <span
      data-testid={`payload-status-${status}`}
      title={status}
      className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide2 border ${cls}`}
    >
      {label}
    </span>
  );
}

export default function IngestLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('today');

  const [filters, setFilters] = useState({
    outcome: '',
    company_code: '',
    mailgun_message_id: '',
    since_hours: '',
    problematic: false,
  });

  const [detail, setDetail] = useState(null);

  const loadStats = useCallback(async () => {
    const { data } = await api.get('/api/audit/ingest/stats', { params: { period } });
    setStats(data);
  }, [period]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === false) return;
        params[k] = v === true ? 'true' : v;
      });
      const { data } = await api.get('/api/audit/ingest', { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { load(); }, [load]);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/api/audit/ingest/${id}`);
      setDetail(data);
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <>
      <PageHeader
        title="Registro Ingesta"
        subtitle={`${total} intentos (filtros activos)`}
        testid="ingest-log-header"
      >
        <Btn variant="secondary" onClick={() => { load(); loadStats(); }} data-testid="ingest-refresh">
          <RefreshCw size={14} /> Actualizar
        </Btn>
      </PageHeader>

      <div className="p-8">
        {/* Stats tiles */}
        <Card testid="ingest-stats" className="mb-6">
          <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wide2">
              Estadísticas periodo
            </h2>
            <Select
              data-testid="ingest-stats-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="w-32"
            >
              <option value="today">Hoy</option>
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5">
            {[
              { k: 'total', label: 'Total', cls: 'border-ink' },
              { k: 'created', label: 'Creados', cls: 'border-ok' },
              { k: 'duplicate', label: 'Duplicados', cls: 'border-warn' },
              { k: 'rejected', label: 'Rechazados', cls: 'border-danger' },
              { k: 'error', label: 'Errores', cls: 'border-danger' },
            ].map(({ k, label, cls }) => (
              <div
                key={k}
                data-testid={`stat-${k}`}
                className={`border-r border-b border-ink/40 ${cls} border-l-4 p-4`}
              >
                <div className="num text-3xl font-semibold leading-none">
                  {stats ? stats[k] : '–'}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wide2 text-muted mt-2">
                  {label}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 mb-4" testid="ingest-filters">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Resultado</Label>
              <Select
                data-testid="filter-outcome"
                value={filters.outcome}
                onChange={(e) => update('outcome', e.target.value)}
                disabled={filters.problematic}
              >
                <option value="">Todos</option>
                <option value="created">created</option>
                <option value="duplicate">duplicate</option>
                <option value="rejected">rejected</option>
                <option value="error">error</option>
              </Select>
            </div>
            <div>
              <Label>Empresa</Label>
              <Input
                data-testid="filter-company"
                value={filters.company_code}
                onChange={(e) => update('company_code', e.target.value.toUpperCase())}
                placeholder="BLU / YOO / DIG"
              />
            </div>
            <div>
              <Label>Mailgun Message-Id</Label>
              <Input
                data-testid="filter-msgid"
                value={filters.mailgun_message_id}
                onChange={(e) => update('mailgun_message_id', e.target.value)}
                placeholder="<abc@mg...>"
              />
            </div>
            <div>
              <Label>Últimas N horas</Label>
              <Input
                data-testid="filter-since"
                type="number"
                min="1"
                value={filters.since_hours}
                onChange={(e) => update('since_hours', e.target.value)}
                placeholder="ej. 24"
              />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 pt-3 border-t border-border">
            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
              <input
                data-testid="filter-problematic"
                type="checkbox"
                checked={filters.problematic}
                onChange={(e) => {
                  // when toggling problematic ON, clear "outcome" to avoid conflicts
                  setFilters((f) => ({
                    ...f,
                    problematic: e.target.checked,
                    outcome: e.target.checked ? '' : f.outcome,
                  }));
                }}
              />
              <span>
                <strong>Solo problemáticos</strong>
                <span className="text-muted"> — rejected, error, no_pdf, failed</span>
              </span>
            </label>
          </div>
        </Card>

        {/* Table */}
        <Card testid="ingest-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-surface text-muted">
                <tr className="text-[10px] uppercase tracking-wide2">
                  <th className="px-3 py-2 text-left font-bold">Cuándo</th>
                  <th className="px-3 py-2 text-left font-bold">Resultado</th>
                  <th className="px-3 py-2 text-left font-bold">Estado doc.</th>
                  <th className="px-3 py-2 text-left font-bold">HTTP</th>
                  <th className="px-3 py-2 text-left font-bold">Emp.</th>
                  <th className="px-3 py-2 text-left font-bold">Remitente / Asunto</th>
                  <th className="px-3 py-2 text-left font-bold">Detalle</th>
                  <th className="px-3 py-2 text-right font-bold">ms</th>
                  <th className="px-3 py-2 text-right font-bold">Doc</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted">Cargando…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-muted">Sin intentos</td></tr>
                )}
                {items.map((a) => {
                  const isProblem =
                    a.outcome === 'rejected' ||
                    a.outcome === 'error' ||
                    a.payload_status === 'no_pdf' ||
                    a.payload_status === 'failed';
                  return (
                    <tr
                      key={a.id}
                      data-testid={`attempt-row-${a.id}`}
                      onClick={() => openDetail(a.id)}
                      className={`border-t border-border cursor-pointer ${isProblem ? 'bg-danger/5 hover:bg-danger/10' : 'hover:bg-surface'}`}
                    >
                      <td className="px-3 py-2 num whitespace-nowrap">{new Date(a.attempted_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><OutcomeBadge outcome={a.outcome} /></td>
                      <td className="px-3 py-2"><PayloadStatusBadge status={a.payload_status} /></td>
                      <td className="px-3 py-2 num">{a.http_status}</td>
                      <td className="px-3 py-2 font-mono">{a.company_code || '—'}</td>
                      <td className="px-3 py-2 max-w-md truncate">
                        <div className="font-mono text-[11px]">{a.sender || '—'}</div>
                        <div className="text-muted text-[11px] truncate">{a.subject || '—'}</div>
                      </td>
                      <td className="px-3 py-2 max-w-xs truncate">{a.outcome_detail || '—'}</td>
                      <td className="px-3 py-2 num text-right">{a.ingest_ms ?? '—'}</td>
                      <td className="px-3 py-2 text-right">
                        {a.document_id ? (
                          <Link
                            to={`/documents/${a.document_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-accent hover:text-accentHover font-bold"
                          >
                            #{a.document_id}
                          </Link>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div
          data-testid="attempt-detail-overlay"
          className="fixed inset-0 bg-ink/40 z-40 flex justify-end"
          onClick={() => setDetail(null)}
        >
          <div
            className="bg-white w-full max-w-2xl h-full overflow-y-auto border-l border-ink"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-ink/90 px-4 py-3 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-sm font-bold uppercase tracking-wide2">
                Intento #{detail.id}
              </h2>
              <button
                data-testid="close-detail"
                onClick={() => setDetail(null)}
                className="p-1 hover:bg-surface"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 text-xs space-y-4">
              <div className="flex items-center gap-2">
                <OutcomeBadge outcome={detail.outcome} />
                <span className="num">HTTP {detail.http_status}</span>
                {detail.ingest_ms != null && <span className="text-muted">— {detail.ingest_ms} ms</span>}
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Cuándo</div>
                <div className="num">{new Date(detail.attempted_at).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Detalle</div>
                <div>{detail.outcome_detail || '—'}</div>
              </div>
              {detail.document_id && (
                <div>
                  <Link to={`/documents/${detail.document_id}`} className="inline-flex items-center gap-1 text-accent hover:text-accentHover font-bold">
                    Ir al documento #{detail.document_id} <ExternalLink size={12} />
                  </Link>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Emp.</div>
                  <div className="font-mono">{detail.company_code || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Destinatario</div>
                  <div className="font-mono">{detail.recipient || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Remitente</div>
                  <div className="font-mono">{detail.sender || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Remitente original</div>
                  <div className="font-mono">{detail.original_sender || '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Asunto</div>
                  <div>{detail.subject || '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Mailgun Message-Id</div>
                  <div className="font-mono break-all">{detail.mailgun_message_id || '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Clave Wasabi</div>
                  <div className="font-mono break-all">{detail.wasabi_key || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Estado payload</div>
                  <div>{detail.payload_status || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Flags</div>
                  <div className="font-mono">{detail.payload_flags?.join(' ') || '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">Error payload</div>
                  <div>{detail.payload_error_message || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">IP</div>
                  <div className="font-mono">{detail.request_ip || '—'}</div>
                </div>
                <div>
                  <div className="text-muted text-[10px] font-bold uppercase tracking-wide2">User-Agent</div>
                  <div className="font-mono truncate" title={detail.request_user_agent}>{detail.request_user_agent || '—'}</div>
                </div>
              </div>
              <div>
                <div className="text-muted text-[10px] font-bold uppercase tracking-wide2 mb-1">Payload raw</div>
                <pre className="bg-surface border border-border p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(detail.raw_payload, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
